import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Calendar, Info, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Separator } from './ui/separator';

// TypeScript types for pricing
type PricingItemSimple = {
  name: string;
  desc?: string;
  price: string;
  vat?: string;
};

type PricingItemCategorized = {
  name: string;
  desc?: string;
  a?: string;
  aVat?: string;
  b?: string;
  bVat?: string;
  c?: string;
  cVat?: string;
};

type PricingItem = PricingItemSimple | PricingItemCategorized;

type PricingSection = {
  title: string;
  subtitle?: string;
  highlight?: boolean;
  items: PricingItem[];
};

const PRICING_SECTIONS: PricingSection[] = [
  {
    title: 'Normohodina',
    subtitle: 'Pracovný čas mechanika podľa kategórie vozidla',
    items: [
      { name: 'Kategória A (stredná trieda)', price: '40€/hod', vat: '49.2€' },
      { name: 'Kategória B (vyššia trieda)', price: '55€/hod', vat: '67.65€' },
      { name: 'Kategória C (luxusné/športové)', price: '75€/hod', vat: '92.25€' },
      { name: 'Kontrola a príprava vozidla na STK', price: '200€', vat: '246€' },
    ],
  },
  {
    title: 'Motor a prevodovka',
    subtitle: 'Základné servisné úkony pre motor a prevodovku',
    items: [
      { name: 'Výmena oleja + filter', a: '80€', aVat: '98.4€', b: '95€', bVat: '116.85€', c: '110€', cVat: '135.3€' },
      { name: 'Výmena rozvodov (timing belt)', price: 'od 400€', vat: 'od 492€' },
      { name: 'Výmena oleja v prevodovke', price: 'od 300€', vat: 'od 369€' },
    ],
  },
  {
    title: 'Brzdový systém',
    subtitle: 'Servis a výmena brzdových komponentov',
    items: [
      { name: 'Výmena brzdových platničiek (náprava)', a: '70€', aVat: '86.1€', b: '90€', bVat: '110.7€', c: '110€', cVat: '135.3€' },
      { name: 'Výmena kotúčov + platničiek (náprava)', a: '90€', aVat: '110.7€', b: '110€', bVat: '135.3€', c: '130€', cVat: '159.9€' },
    ],
  },
  {
    title: 'Diagnostika a kódovanie',
    subtitle: 'Profesionálna TOPDON diagnostika s priamym prístupom k riadiacim jednotkám (nie len OBD2). Dokážeme diagnostikovať a nastaviť aj skryté funkcie výrobcu.',
    highlight: true,
    items: [
      { name: 'Komplexná diagnostika (Topdon + ISTA)', desc: 'Hlbšia diagnostika ako bežné OBD2 skenery', a: '30€', aVat: '36.9€', b: '40€', bVat: '49.2€', c: '50€', cVat: '61.5€' },
      { name: 'Kódovanie riadiacich jednotiek', desc: 'Aktivácia skrytých funkcií, nastavenie modulu', price: 'Dohodou' },
    ],
  },
  {
    title: 'Geometria náprav 3D Hunter',
    subtitle: '🏆 Najmodernejší 3D systém Hunter. Špecializujeme sa na všetky značky vrátane prémiových (Mercedes, BMW, Audi). Každé auto testujeme jazdou a dotiahnutím nastavenia na dokonalosť.',
    highlight: true,
    items: [
      { name: 'Geometria všetkých 4 kolies (3D meranie)', desc: 'Presnosť na 0,01° - testované jazdou', price: 'od 70€', vat: 'od 86.1€' },
    ],
  },
  {
    title: 'ADAS kalibrácia',
    subtitle: 'Kalibrácia asistenčných systémov (kamery, radary)',
    items: [
      { name: 'Kalibrácia kamier a radarov', a: '120€', aVat: '147.6€', b: '150€', bVat: '184.5€', c: '180€', cVat: '221.4€' },
    ],
  },
  {
    title: 'Klimatizácia',
    subtitle: 'Servis klimatizačného systému',
    items: [
      { name: 'Dezinfekcia klimatizácie ozónom', price: '35€', vat: '43.05€' },
      { name: 'Plnenie R1234yf (nové chladivo od 2017)', price: '60€ + 0.25€/g', vat: '73.8€ + 0.31€/g' },
      { name: 'Plnenie R134a (staršie vozidlá)', price: '40€ + 0.15€/g', vat: '49.2€ + 0.18€/g' },
    ],
  },
  {
    title: 'Pneuservis',
    subtitle: '💎 Prémiová automatická prezúvačka HOFMANN. Bezpečná aj pre najdrahšie alu disky - žiadne škrabance ani poškodenie. Ideálne pre veľké elektróny (21"+).',
    highlight: true,
    items: [
      { name: 'Prezutie + vyváženie R15', desc: 'Štandardné kolesá', price: '50€', vat: '61.5€' },
      { name: 'Prezutie + vyváženie R16-18', desc: 'Najčastejšie rozmery', price: '60€', vat: '73.8€' },
      { name: 'Prezutie + vyváženie R19-20', desc: 'Veľké disky', price: '80€', vat: '98.4€' },
      { name: 'Prezutie + vyváženie R21-22', desc: 'Prémiové/elektromobily', price: '100€', vat: '123€' },
      { name: 'Prezutie + vyváženie R23-24', desc: 'Extra veľké disky', price: '120€', vat: '147.6€' },
      { name: 'Runflat + príplatok', desc: 'Pneumatiky s vyztuženými bokmi', price: '+20€', vat: '+24.6€' },
      { name: 'Nízkoprofilové + príplatok', desc: 'Profil 35 a menej', price: '+20€', vat: '+24.6€' },
    ],
  },
];

export const Pricing = () => {
  return (
    <section className="relative py-16 overflow-hidden bg-zinc-950">
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-lime-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-lime-600/5 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge 
            variant="secondary"
            className="mb-6 px-6 py-2 bg-zinc-900/50 border-lime-500/20"
          >
            <span className="text-sm font-medium text-lime-400">Transparentné ceny</span>
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Cenník služieb
          </h2>
          <p className="text-xl text-zinc-400 mb-6">
            Férové ceny bez skrytých poplatkov
          </p>

          {/* Info badge */}
          <Badge 
            variant="outline" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900/50 border-zinc-800"
          >
            <Info size={16} className="text-lime-400" />
            <span className="text-zinc-300">Všetky ceny bez DPH · <span className="text-lime-400">+23% s DPH</span></span>
          </Badge>
        </motion.div>

        {/* Modern Vehicle Categories */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <Card className="bg-zinc-900/50 backdrop-blur-sm border-zinc-800">
            <div className="p-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wide text-center mb-6">
                Kategórie vozidiel
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 bg-zinc-900/70 border-zinc-800 hover:border-lime-500/40 transition-all text-center">
                  <Badge className="mb-3 bg-lime-400 text-black hover:bg-lime-400">
                    Kategória A
                  </Badge>
                  <p className="text-xs text-zinc-500 mb-2">Stredná trieda</p>
                  <p className="text-sm text-zinc-300">VW Golf, Škoda Octavia, Ford Focus</p>
                </Card>
                <Card className="p-4 bg-zinc-900/70 border-zinc-800 hover:border-lime-500/40 transition-all text-center">
                  <Badge className="mb-3 bg-lime-400 text-black hover:bg-lime-400">
                    Kategória B
                  </Badge>
                  <p className="text-xs text-zinc-500 mb-2">Vyššia trieda</p>
                  <p className="text-sm text-zinc-300">BMW 3, Audi A4, Mercedes C-Class</p>
                </Card>
                <Card className="p-4 bg-zinc-900/70 border-zinc-800 hover:border-lime-500/40 transition-all text-center">
                  <Badge className="mb-3 bg-lime-400 text-black hover:bg-lime-400">
                    Kategória C
                  </Badge>
                  <p className="text-xs text-zinc-500 mb-2">Luxusné a športové</p>
                  <p className="text-sm text-zinc-300">Porsche, BMW M, Audi RS, Mercedes AMG</p>
                </Card>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Modern Pricing Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {PRICING_SECTIONS.map((section: PricingSection, idx: number) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className={`group overflow-hidden transition-all hover:shadow-xl ${
                section.highlight 
                  ? 'bg-zinc-900/70 border-lime-500/40 shadow-lg shadow-lime-500/10' 
                  : 'bg-zinc-900/50 border-zinc-800 hover:border-lime-500/20'
              }`}>
                
                {/* Section Header */}
                <div className={`px-6 py-4 border-b ${
                  section.highlight 
                    ? 'bg-lime-500/10 border-lime-500/20' 
                    : 'bg-zinc-900/50 border-zinc-800'
                }`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className={`text-lg font-bold mb-1 flex items-center gap-2 ${
                        section.highlight ? 'text-lime-400' : 'text-white'
                      }`}>
                        {section.highlight && <Sparkles className="w-4 h-4" />}
                        {section.title}
                      </h3>
                      {section.subtitle && (
                        <p className={`text-xs leading-relaxed ${
                          section.highlight ? 'text-zinc-300' : 'text-zinc-500'
                        }`}>
                          {section.subtitle}
                        </p>
                      )}
                    </div>
                    {section.highlight && (
                      <Badge className="bg-lime-400 text-black hover:bg-lime-400 text-xs font-semibold">
                        Top
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Items List */}
                <div className="divide-y divide-zinc-800">
                  {section.items.map((item: PricingItem, itemIdx: number) => (
                    <div
                      key={itemIdx}
                      className="px-6 py-4 hover:bg-zinc-900/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <span className="text-white text-sm font-medium block">{item.name}</span>
                          {item.desc && (
                            <span className="text-zinc-500 text-xs block mt-1">{item.desc}</span>
                          )}
                        </div>
                        
                        {'price' in item ? (
                          // Simple price
                          <div className="text-right flex-shrink-0">
                            <span className="text-white font-semibold text-sm whitespace-nowrap">{item.price}</span>
                            {item.vat && (
                              <span className="text-zinc-500 text-xs block mt-0.5">({item.vat} s DPH)</span>
                            )}
                          </div>
                        ) : (
                          // Category prices A/B/C
                          <div className="flex items-center gap-4 text-sm flex-shrink-0">
                            {item.a && (
                            <div className="text-center">
                              <Badge variant="outline" className="mb-1 h-5 px-1.5 text-xs border-zinc-700">A</Badge>
                              <div className="text-white font-semibold">{item.a}</div>
                              {item.aVat && (
                                <div className="text-zinc-500 text-xs mt-0.5">({item.aVat})</div>
                              )}
                            </div>
                            )}
                            {item.b && (
                            <div className="text-center">
                              <Badge variant="outline" className="mb-1 h-5 px-1.5 text-xs border-zinc-700">B</Badge>
                              <div className="text-white font-semibold">{item.b}</div>
                              {item.bVat && (
                                <div className="text-zinc-500 text-xs mt-0.5">({item.bVat})</div>
                              )}
                            </div>
                            )}
                            {item.c && (
                            <div className="text-center">
                              <Badge variant="outline" className="mb-1 h-5 px-1.5 text-xs border-zinc-700">C</Badge>
                              <div className="text-white font-semibold">{item.c}</div>
                              {item.cVat && (
                                <div className="text-zinc-500 text-xs mt-0.5">({item.cVat})</div>
                              )}
                            </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Modern Footer Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="bg-zinc-900/50 backdrop-blur-sm border-zinc-800">
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Contact */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-lime-400" />
                    Kontakt
                  </h3>
                  <div className="space-y-3 text-sm">
                    <a 
                      href="tel:+421910666949" 
                      className="flex items-center gap-3 text-zinc-400 hover:text-lime-400 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center group-hover:bg-zinc-700 transition-colors">
                        <Phone size={16} />
                      </div>
                      <span>+421 910 666 949</span>
                    </a>
                    <a 
                      href="mailto:servis@blackrent.sk" 
                      className="flex items-center gap-3 text-zinc-400 hover:text-lime-400 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center group-hover:bg-zinc-700 transition-colors">
                        <Mail size={16} />
                      </div>
                      <span>servis@blackrent.sk</span>
                    </a>
                    <div className="flex items-center gap-3 text-zinc-400">
                      <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center">
                        <MapPin size={16} />
                      </div>
                      <span>Bratislavská 971, 91105 Trenčín</span>
                    </div>
                  </div>
                </div>

                {/* Important Info */}
                <div>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5 text-lime-400" />
                    Dôležité informácie
                  </h3>
                  <ul className="space-y-2 text-sm text-zinc-400">
                    <li className="flex items-start gap-2">
                      <span className="text-lime-400 mt-0.5">•</span>
                      <span>Doporučujeme si vopred dohodnúť termín</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-lime-400 mt-0.5">•</span>
                      <span>V prípade nečakaných prác Vás vždy kontaktujeme</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-lime-400 mt-0.5">•</span>
                      <span>Platba je splatná ihneď po dokončení služby</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-lime-400 mt-0.5">•</span>
                      <span>Prijímame hotovosť a bankový prevod</span>
                    </li>
                  </ul>
                </div>
              </div>

              <Separator className="mb-8 bg-zinc-800" />

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild
                  size="lg"
                  className="px-8 py-6 bg-lime-400 hover:bg-lime-300 text-black font-semibold rounded-xl transition-all hover:scale-105 shadow-lg shadow-lime-500/20"
                >
                  <Link to="/rezervacia" className="flex items-center gap-2">
                    <Calendar size={18} />
                    Objednať sa online
                  </Link>
                </Button>
                <Button 
                  asChild
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 border-2 border-zinc-700 hover:border-lime-500 bg-zinc-900/50 hover:bg-zinc-900 text-white font-semibold rounded-xl transition-all"
                >
                  <a href="tel:+421910666949" className="flex items-center gap-2">
                    <Phone size={18} />
                    Zavolať nám
                  </a>
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
